import { daysUntil, type Assessment, type MessageLadder } from '../../lib/api'
import type { StageState, Tone } from '../kit'
import { questionnaireWord } from '../../lib/status'

// How a questionnaire, a reminder and an exit read as a journey: which stage
// they are on, and the one or two words that say so.
//
// # WHY THE MAPPING LIVES IN ONE FILE
//
// Home, the Assessments list, the tracker in its drawer and the vendor's
// Messages tab all draw the same journeys. When each worked out "where is this
// questionnaire" for itself, Home called a questionnaire overdue while the list
// called it returned. One mapping means one answer, and every answer here is
// read from a status or a date the API returned: nothing is estimated.

// A questionnaire sent back for more answers is with the vendor again, and is
// as late as an unanswered one once its date passes.
export const OPEN = new Set(['ISSUED', 'IN_PROGRESS', 'RETURNED'])
export const REVIEW = new Set(['SUBMITTED', 'UNDER_REVIEW'])
export const SCORED = new Set(['SCORED', 'ACCEPTED', 'PROVISIONAL'])

// The stages a questionnaire passes through. Each is named as a step, and the
// steps that are also states use the vocabulary's word for that state; the
// vendor's step is "Vendor answers" whether it is not started, in progress or
// late, because the chip beside the track already says which.
export const QUESTIONNAIRE_STAGES = ['Sent', 'Vendor answers', 'Submitted', 'Scored'] as const

export function isLate(a: Assessment): boolean {
  const d = daysUntil(a.dueOn)
  return OPEN.has(a.status) && d != null && d < 0
}

// The four stages of a questionnaire from its status. Expired and cancelled
// questionnaires stop at Answering with a cross, because that is where they
// ended; a provisional score from a document has every stage behind it.
export function questionnaireStates(status: string): StageState[] {
  switch (status) {
    case 'ISSUED':
    case 'IN_PROGRESS':
    case 'RETURNED':
      return ['done', 'current', 'upcoming', 'upcoming']
    case 'SUBMITTED':
    case 'UNDER_REVIEW':
      return ['done', 'done', 'current', 'upcoming']
    case 'SCORED':
    case 'ACCEPTED':
    case 'PROVISIONAL':
      return ['done', 'done', 'done', 'done']
    case 'EXPIRED':
    case 'CANCELLED':
      return ['done', 'rejected', 'upcoming', 'upcoming']
    default:
      return ['upcoming', 'upcoming', 'upcoming', 'upcoming']
  }
}

export function questionnaireTrackLabel(status: string): string {
  const states = questionnaireStates(status)
  const at = states.findIndex((s) => s !== 'done')
  if (at < 0) return 'All four stages done'
  return 'Stage ' + (at + 1) + ' of 4, ' + QUESTIONNAIRE_STAGES[at]?.toLowerCase()
}

// The Progress cell of the Assessments list (release 3.3, O1 of the 7 Oct
// plan: the column was found empty). A questionnaire that has gone out shows
// the four dots and the name of the stage it is on; one that has not gone
// (a draft, one waiting for a second person, one an approver turned down)
// says "Not sent", because four empty dots read as a blank cell.
export type QuestionnaireProgress =
  | { sent: false; word: 'Not sent' }
  | { sent: true; states: StageState[]; label: string; word: string }

export function questionnaireProgress(status: string): QuestionnaireProgress {
  const states = questionnaireStates(status)
  if (states[0] !== 'done') return { sent: false, word: 'Not sent' }
  const at = states.findIndex((s) => s !== 'done')
  const word = at < 0 ? QUESTIONNAIRE_STAGES[3] : QUESTIONNAIRE_STAGES[at] ?? ''
  return { sent: true, states, label: questionnaireTrackLabel(status), word }
}

// A questionnaire's state in the vocabulary's words (lib/status.ts), with
// Overdue laid over one still with the vendor past its date.
export function questionnaireStatus(a: Assessment): { tone: Tone; text: string } {
  const s = questionnaireWord(a.status, isLate(a))
  return { tone: s.tone, text: s.word }
}

// ------------------------------------------------------------------ reminders

export function channelName(channel: string): string {
  switch (channel) {
    case 'WHATSAPP':
      return 'WhatsApp'
    case 'SMS':
      return 'SMS'
    case 'EMAIL':
      return 'Email'
    default:
      return words(channel)
  }
}

// What became of one rung, in the words a person chasing a vendor uses. QUEUED
// is the rung in flight: handed to the carrier or about to be, with no receipt
// yet, and the ladder waits on it before trying the next channel.
export function rungWord(outcome: string): { tone: Tone; text: string } {
  switch (outcome) {
    case 'DELIVERED':
      return { tone: 'ok', text: 'Delivered' }
    case 'READ':
      return { tone: 'ok', text: 'Read' }
    case 'RESPONDED':
      return { tone: 'ok', text: 'Replied' }
    case 'QUEUED':
      return { tone: 'brand', text: 'Waiting' }
    case 'FAILED':
      return { tone: 'crit', text: 'Not delivered' }
    case 'NO_ANSWER':
      return { tone: 'warn', text: 'No answer' }
    case 'SKIPPED':
      return { tone: 'neutral', text: 'Skipped' }
    default:
      return { tone: 'neutral', text: words(outcome) }
  }
}

export function ladderWord(state: MessageLadder['state'] | string): { tone: Tone; text: string } {
  switch (state) {
    case 'RUNNING':
      return { tone: 'brand', text: 'Trying' }
    case 'DELIVERED':
      return { tone: 'ok', text: 'Delivered' }
    case 'RESPONDED':
      return { tone: 'ok', text: 'Replied' }
    case 'EXHAUSTED':
      return { tone: 'crit', text: 'Not reached' }
    case 'STOPPED':
      return { tone: 'plain', text: 'Stopped' }
    default:
      return { tone: 'neutral', text: words(state) }
  }
}

// The rungs a ladder has actually tried, as stages. The read returns only rungs
// that were queued, settled or skipped, never the channels still to come, so a
// running ladder ends at the rung in flight rather than at a guess of what is
// next. A ladder that ran out ends on a cross.
export function ladderStates(l: MessageLadder): StageState[] {
  const rungs = [...l.rungs].sort((a, b) => a.rungNo - b.rungNo)
  return rungs.map((r, i) => {
    if (r.outcome === 'QUEUED') return 'current'
    if (l.state === 'EXHAUSTED' && i === rungs.length - 1) return 'rejected'
    return 'done'
  })
}

// The newest ladder for one questionnaire, and a line for a table cell.
export function latestLadder(ladders: MessageLadder[], assessmentId: string): MessageLadder | undefined {
  return ladders
    .filter((l) => l.assessmentId === assessmentId)
    .sort((a, b) => b.startedAt.localeCompare(a.startedAt))[0]
}

// ---------------------------------------------------------------------- exits

export type ExitStageLike = { key: string; job: string; state: string }

// Signed stages are done, a declined stage is a cross, and the first stage
// still outstanding is where the exit is now, blocked when the caller knows
// something is in its way.
export function exitStates(stages: ExitStageLike[], blocked: boolean): StageState[] {
  const first = stages.findIndex((s) => s.state !== 'signed' && s.state !== 'declined')
  return stages.map((s, i) => {
    if (s.state === 'signed') return 'done'
    if (s.state === 'declined') return 'rejected'
    if (i === first) return blocked ? 'blocked' : 'current'
    return 'upcoming'
  })
}

// ---------------------------------------------------------------------- dates

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

// "15 Sep" for a date or an instant, read in the viewer's own zone. Month names
// are fixed rather than taken from the locale, which writes "Sept" in en-IN.
export function dayMonth(iso: string | undefined | null): string {
  if (!iso) return ''
  const d = /^\d{4}-\d{2}-\d{2}$/.test(iso) ? new Date(iso + 'T00:00:00') : new Date(iso)
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10)
  return String(d.getDate()).padStart(2, '0') + ' ' + MONTHS[d.getMonth()]
}

// "16 Sep 10:00", for a receipt.
export function dayMonthTime(iso: string | undefined | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return dayMonth(iso) + ' ' + String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0')
}

// "Tue 15 Sep 2026".
export function longToday(now: Date): string {
  return DAYS[now.getDay()] + ' ' + String(now.getDate()).padStart(2, '0') + ' ' + MONTHS[now.getMonth()] + ' ' + now.getFullYear()
}

export function words(code: string): string {
  const s = code.toLowerCase().replace(/_/g, ' ')
  return s.charAt(0).toUpperCase() + s.slice(1)
}

// A sentence for a toggletip, cut to its 200 character limit at a word.
export function tipText(s: string): string {
  const t = s.trim()
  if (t.length <= 200) return t
  const cut = t.slice(0, 199)
  const at = cut.lastIndexOf(' ')
  return (at > 150 ? cut.slice(0, at) : cut) + '…'
}
